import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Paperclip, Headphones, ShoppingCart, MailWarning, Check, Send, Undo2, RefreshCw } from 'lucide-react';
import { formatTelefoneBR, contar } from '@fiodecobre/shared';
import { Modal } from '../../../components/Modal';
import { Avatar } from '../../../components/Avatar';
import { useDialog } from '../../../components/DialogProvider';
import { useSocket } from '../../../lib/socket';
import { atendimentoApi } from '../lib/api';

interface Props {
  aberto: boolean;
  onFechar: () => void;
}

type Dados = Awaited<ReturnType<typeof atendimentoApi.mensagensSemConfirmacao>>;
type Item = Dados['itens'][number];

const PERIODOS = [
  { dias: 1, rotulo: 'Hoje' },
  { dias: 7, rotulo: '7 dias' },
  { dias: 30, rotulo: '30 dias' },
];

// Acima disto o canal esta fora do padrao — a mesma regua da faixa do topo (DA-503).
const PCT_CANAL_DOENTE = 15;

function idade(seg: number): string {
  const min = Math.floor(seg / 60);
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  return h < 24 ? `${h}h` : `${Math.floor(h / 24)}d`;
}

function ident(p: Item): string {
  const fone = p.telefone ? formatTelefoneBR(p.telefone) : '';
  if (p.nome) return fone ? `${p.nome} · ${fone}` : p.nome;
  return fone || '—';
}

// Painel "Mensagens nao entregues" (DA-504/DA-505). Duas portas: a faixa do topo (que so acende
// quando o canal foge do padrao) e a engrenagem do Atendimento, que e a porta PERMANENTE —
// recurso que so existe enquanto o alarme esta aceso e' recurso que ninguem lembra que tem.
//
// ⚠️ O titulo diz "nao entregues" porque foi assim que o Ruan nomeou a ferramenta, mas cada linha
// fala em "sem confirmacao", e o rodape explica a diferenca: medido em 11/08, 14% das entregas
// BOAS so confirmam depois de 5min e 4% depois de 1h. A lista e um ponto de PARTIDA pra conferir,
// nao um veredito — por isso ela nao esconde nada e nao ordena por "gravidade" inventada.
//
// Em TELA CHEIA (DA-456) porque virou tela de trabalho: cada linha ganhou duas acoes (DA-505) e,
// na caixa de 780px, os botoes disputavam largura com o proprio texto da mensagem — o que se
// precisa ler pra decidir se reenvia.
export function ModalMensagensNaoEntregues({ aberto, onFechar }: Props) {
  const dialog = useDialog();
  const navigate = useNavigate();
  const { socket } = useSocket();
  const [carregando, setCarregando] = useState(false);
  const [dias, setDias] = useState(1);
  const [mostrarCientes, setMostrarCientes] = useState(false);
  const [dados, setDados] = useState<Dados | null>(null);
  // Ids DESTA lista cujo recibo chegou com o painel aberto. Nao e um contador de trafego: so
  // conta quem esta na tela agora, senao o aviso acenderia por mensagem que ninguem esta vendo.
  const [confirmadasAgora, setConfirmadasAgora] = useState<Set<number>>(new Set());
  const idsNaTelaRef = useRef<Set<number>>(new Set());
  // Ids em voo — trava o par de botoes daquela linha (reenviar duas vezes por clique duplo
  // manda duas mensagens ao cliente, e isso nao tem desfazer).
  const [ocupados, setOcupados] = useState<Set<number>>(new Set());

  // ⚠️ `dialog` FORA das dependencias, por um ref. O `useDialog()` devolve um objeto NOVO a cada
  // render do provider — e o provider re-renderiza ao abrir e ao fechar qualquer confirmacao.
  // Com ele na lista, abrir a confirmacao do "Ciente para todos" re-disparava a carga, e a
  // resposta dessa carga (pedida ANTES da gravacao) chegava DEPOIS e repintava as 9 linhas que
  // acabaram de sair. Medido em prod: servidor com 44 itens, tela com 53.
  const dialogRef = useRef(dialog);
  useEffect(() => { dialogRef.current = dialog; }, [dialog]);

  // Guarda de corrida (a receita do CLAUDE.md § Codigo): so a consulta MAIS NOVA pode escrever.
  // Vale pra qualquer `setState` da carga, inclusive o do erro e o do loading — resposta
  // descartada apagando o spinner de uma consulta em voo e o mesmo defeito com outra roupa.
  const reqId = useRef(0);
  const carregar = useCallback(async () => {
    const req = ++reqId.current;
    setCarregando(true);
    try {
      const r = await atendimentoApi.mensagensSemConfirmacao(dias, mostrarCientes);
      if (req !== reqId.current) return;
      setDados(r);
      setConfirmadasAgora(new Set());
    } catch {
      if (req !== reqId.current) return;
      dialogRef.current.toast('Falha ao carregar as mensagens sem confirmacao', { tipo: 'erro' });
    } finally {
      if (req === reqId.current) setCarregando(false);
    }
  }, [dias, mostrarCientes]);

  // Acao que ja escreveu no banco INVALIDA qualquer carga em voo — senao a resposta velha
  // (pedida antes da gravacao) ganha da tela nova so por chegar depois.
  function invalidarCargasEmVoo() {
    reqId.current++;
  }

  // O recibo que chega vira AVISO no botao, nunca sumico automatico da linha. Tirar a linha
  // sozinha mexeria a lista debaixo do dedo de quem esta descendo ela — e aqui o clique vizinho
  // e "Reenviar", que manda WhatsApp de verdade pro cliente errado e nao tem desfazer (DA-298:
  // recarga de fundo nao pode se comportar como acao pedida). Quem decide quando a lista muda
  // e' quem esta olhando pra ela.
  //
  // O filtro le a lista por um `ref` espelhado, nao de dentro de um updater de `setState`:
  // updater roda no render seguinte (e duas vezes em StrictMode), entao decidir la dentro le
  // estado velho e ainda dispara efeito colateral em funcao que deveria ser pura.
  useEffect(() => {
    if (!aberto || !socket) return;
    const aoStatus = (p: { id: number; status?: string }) => {
      if (p.status === 'sent') return; // reenvio da fila do Baileys: continua sem recibo
      if (!idsNaTelaRef.current.has(p.id)) return;
      setConfirmadasAgora((s) => (s.has(p.id) ? s : new Set(s).add(p.id)));
    };
    socket.on('message_status', aoStatus);
    return () => { socket.off('message_status', aoStatus); };
  }, [aberto, socket]);

  // Carga unica: abrir o painel, trocar de periodo e ligar o "Mostrar cientes" passam todos por
  // `carregar`. Havia uma SEGUNDA copia desta logica aqui — e foi a copia que trouxe o defeito
  // de volta (ela tinha `dialog` nas dependencias e nenhuma guarda de corrida). Dois caminhos
  // que precisam devolver a mesma coisa: o certo e nao existirem dois.
  useEffect(() => {
    if (!aberto) return;
    void carregar();
  }, [aberto, carregar]);

  // Agrupa por conversa preservando a ordem que veio do banco (mais recente primeiro): a
  // atendente pensa por PESSOA, nao por mensagem solta.
  // `primeiro` e guardado na criacao do grupo (e nao lido como `itens[0]` depois) porque com
  // `noUncheckedIndexedAccess` o indice 0 e `T | undefined` — aqui ele nunca e, por construcao.
  const grupos = useMemo(() => {
    const mapa = new Map<number, { chave: number; primeiro: Item; itens: Item[] }>();
    for (const it of dados?.itens ?? []) {
      const g = mapa.get(it.conversa_id);
      if (g) g.itens.push(it);
      else mapa.set(it.conversa_id, { chave: it.conversa_id, primeiro: it, itens: [it] });
    }
    return [...mapa.values()];
  }, [dados]);

  // Espelho pro listener do socket (ver o efeito acima).
  useEffect(() => {
    idsNaTelaRef.current = new Set((dados?.itens ?? []).map((i) => i.id));
  }, [dados]);

  function abrirConversa(conversaId: number) {
    localStorage.setItem('fiodecobre_conversa_ativa', String(conversaId));
    onFechar();
    navigate('/atendimento');
  }

  function ocupar(id: number, on: boolean) {
    setOcupados((s) => {
      const n = new Set(s);
      if (on) n.add(id); else n.delete(id);
      return n;
    });
  }

  // Ciente NAO afirma que o cliente recebeu — afirma que alguem olhou e resolveu. Sem
  // confirmacao de proposito: e reversivel (o "Mostrar cientes" traz de volta com o Desfazer),
  // e portao em acao que se desfaz num clique so treina o operador a clicar sem ler.
  async function marcarCiente(it: Item, ciente: boolean) {
    ocupar(it.id, true);
    try {
      await atendimentoApi.marcarEntregaCiente([it.id], ciente);
      invalidarCargasEmVoo();
      // Escondendo: tira a linha na hora em vez de recarregar — a lista pularia por baixo do
      // dedo de quem esta descendo ela e o proximo clique cairia noutra mensagem.
      // Mostrando os cientes: recarrega, porque quem/quando da ciencia sao dados do SERVIDOR;
      // inventar "agora" no estado local poria na tela um carimbo que ninguem gravou.
      if (ciente && !mostrarCientes) {
        setDados((d) => d && { ...d, itens: d.itens.filter((x) => x.id !== it.id), cientes: d.cientes + 1 });
      } else {
        await carregar();
      }
    } catch (e) {
      dialogRef.current.toast((e as Error).message || 'Falha ao marcar como ciente', { tipo: 'erro' });
    } finally {
      ocupar(it.id, false);
    }
  }

  // "Ciente para todos" — o caso comum e' o contato inteiro ja resolvido (ele conferiu com a
  // pessoa, ou o cliente respondeu por outro caminho), e marcar 20 linhas uma a uma nao e' um
  // botao, e' uma tarefa.
  //
  // ⚠️ Aqui a confirmacao EXISTE, e a linha sozinha nao tem — a regra nao e "acao em lote assusta",
  // e o custo do erro: uma linha se desfaz num clique que esta na propria linha; o grupo inteiro
  // SOME da tela, e desfazer exige ligar o "Mostrar cientes", achar o contato e clicar de novo.
  // Portao se paga onde a volta e' cara.
  async function cienteDoGrupo(primeiro: Item, itens: Item[]) {
    const quem = primeiro.nome || (primeiro.telefone ? formatTelefoneBR(primeiro.telefone) : 'este contato');
    const ok = await dialog.confirmar(
      `Marcar ${contar(itens.length, 'mensagem')} de ${quem} como ciente?\n\n` +
      'Elas saem desta lista. Nao avisa o cliente e nao reenvia nada.',
      { titulo: 'Ciente para todos', textoConfirmar: 'Marcar todas' },
    );
    if (!ok) return;
    const ids = itens.map((i) => i.id);
    ids.forEach((id) => ocupar(id, true));
    try {
      await atendimentoApi.marcarEntregaCiente(ids, true);
      invalidarCargasEmVoo();
      if (!mostrarCientes) {
        const fora = new Set(ids);
        setDados((d) => d && { ...d, itens: d.itens.filter((x) => !fora.has(x.id)), cientes: d.cientes + ids.length });
      } else {
        await carregar();
      }
    } catch (e) {
      dialogRef.current.toast((e as Error).message || 'Falha ao marcar como ciente', { tipo: 'erro' });
    } finally {
      ids.forEach((id) => ocupar(id, false));
    }
  }

  // A volta do lote. Sem confirmacao: devolver linha pra lista nao esconde nada de ninguem —
  // o erro aqui custa ver de novo o que ja estava la.
  async function desfazerDoGrupo(itens: Item[]) {
    const ids = itens.map((i) => i.id);
    ids.forEach((id) => ocupar(id, true));
    try {
      await atendimentoApi.marcarEntregaCiente(ids, false);
      await carregar();
    } catch (e) {
      dialogRef.current.toast((e as Error).message || 'Falha ao desfazer', { tipo: 'erro' });
    } finally {
      ids.forEach((id) => ocupar(id, false));
    }
  }

  // Reenviar sai da loja e nao volta (principio 4), e a chance de a mensagem ja ter chegado e
  // ALTA — 14% das entregas boas so confirmam depois de 5min, 4% depois de 1h (DA-503 p.1).
  // Por isso a confirmacao nomeia a consequencia (o cliente recebe DUAS vezes) em vez de um
  // "tem certeza?" generico. O backend ainda reconfere o recibo na hora e recusa se chegou.
  async function reenviar(it: Item) {
    const quem = it.nome || (it.telefone ? formatTelefoneBR(it.telefone) : 'este contato');
    const ok = await dialog.confirmar(
      `Mandar de novo para ${quem}?\n\n"${it.preview || it.tipo}" (de ${it.quando})\n\n` +
      'Mensagem sem confirmacao costuma chegar depois — se ela ja chegou, o cliente vai receber duas vezes. ' +
      'Confirme por outro caminho antes, se der.',
      { titulo: 'Reenviar mensagem', textoConfirmar: 'Reenviar' },
    );
    if (!ok) return;
    ocupar(it.id, true);
    try {
      await atendimentoApi.reenviarMensagem(it.id);
      invalidarCargasEmVoo();
      dialogRef.current.toast(`Reenviada para ${quem}.`, { tipo: 'sucesso' });
      if (!mostrarCientes) {
        setDados((d) => d && { ...d, itens: d.itens.filter((x) => x.id !== it.id), cientes: d.cientes + 1 });
      } else {
        await carregar();
      }
    } catch (e) {
      // Recusa do backend ("ja foi entregue", "janela de 24h fechou", "arquivo saiu do
      // servidor") e informacao, nao ruido de canto de tela: vai em modal, com o texto que
      // a rota escreveu. Depois recarrega, porque o estado da tela ficou defasado.
      await dialog.alerta((e as Error).message || 'Falha ao reenviar', { titulo: 'Nao reenviei', tipo: 'erro' });
      void carregar();
    } finally {
      ocupar(it.id, false);
    }
  }

  const canal = dados?.canal;
  const canalDoente = !!canal && canal.pct >= PCT_CANAL_DOENTE && canal.sem_confirmacao > 0;

  return (
    <Modal aberto={aberto} onFechar={onFechar} titulo="Mensagens não entregues" telaCheia>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Estado do CANAL primeiro: e o que separa "o WhatsApp parou" de "uns celulares
            desligados". Sem ele a lista sozinha nao deixa tirar conclusao nenhuma. */}
        {canal && (
          <div
            style={{
              background: canalDoente ? '#b45309' : 'var(--page-bg)',
              color: canalDoente ? '#000' : 'var(--text-color)',
              border: `1px solid ${canalDoente ? '#92400e' : 'var(--border)'}`,
              borderRadius: 8, padding: '8px 12px', fontSize: 13,
            }}
          >
            {canalDoente ? (
              <>
                <b>O WhatsApp parece nao estar entregando.</b>{' '}
                {canal.sem_confirmacao} de {canal.total} mensagens das ultimas {canal.janela_h}h sem
                confirmacao (<b>{canal.pct}%</b>) — o normal e ate 2%. Confirme por outro caminho antes de reenviar.
              </>
            ) : (
              <>
                Canal <b>normal</b>: {canal.sem_confirmacao} de {canal.total} sem confirmacao nas
                ultimas {canal.janela_h}h ({canal.pct}%). As linhas abaixo provavelmente sao celulares desligados.
              </>
            )}
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {PERIODOS.map((p) => (
            <button
              key={p.dias}
              type="button"
              onClick={() => setDias(p.dias)}
              className={p.dias === dias ? 'btn-primario' : ''}
              style={p.dias === dias ? { flex: '0 0 auto' } : {
                flex: '0 0 auto', background: 'transparent', color: 'var(--text-color)',
                border: '1px solid var(--border)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
              }}
            >
              {p.rotulo}
            </button>
          ))}

          {/* O caminho de volta do "Ciente". Ele mostra a contagem mesmo em zero enquanto
              houver alguma no periodo — e o que impede a lista curta de ler como painel quebrado. */}
          {(dados?.cientes ?? 0) > 0 && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginLeft: 4 }}>
              <input type="checkbox" checked={mostrarCientes} onChange={(e) => setMostrarCientes(e.target.checked)} />
              Mostrar cientes ({dados?.cientes})
            </label>
          )}

          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-color)', opacity: 0.75 }}>
            {carregando ? '' : `${contar(dados?.itens.length ?? 0, 'mensagem')} em ${contar(grupos.length, 'conversa')}`}
          </span>

          {/* Relê o estado das linhas: quem ja confirmou sai da lista. NAO vai perguntar ao
              WhatsApp — recibo chega sozinho quando chega, e este botao so mostra o que ja
              chegou. Acende com a contagem quando o socket avisa que alguma DESTA lista
              confirmou; sem novidade ele continua ali, porque o painel tambem envelhece por
              mensagem nova entrando e o operador nao tem como saber disso sem clicar. */}
          <button
            type="button"
            onClick={() => void carregar()}
            disabled={carregando}
            title="Reler o estado das mensagens desta lista"
            style={confirmadasAgora.size > 0
              ? { ...btnBase, padding: '6px 12px', fontSize: 12, background: '#fde68a', color: '#000', border: '1px solid #d4a017' }
              : { ...btnBase, padding: '6px 12px', fontSize: 12, background: 'transparent', color: 'var(--text-color)', border: '1px solid var(--border)' }}
          >
            <RefreshCw size={13} className={carregando ? 'animate-spin' : undefined} />
            {confirmadasAgora.size > 0
              ? `Atualizar — ${confirmadasAgora.size} ${confirmadasAgora.size === 1 ? 'confirmou' : 'confirmaram'}`
              : 'Atualizar'}
          </button>
        </div>

        {carregando && (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 28 }}>
            <Loader2 className="animate-spin" size={22} />
          </div>
        )}

        {!carregando && grupos.length === 0 && (
          <div style={{ textAlign: 'center', padding: 28, color: 'var(--text-color)', opacity: 0.7 }}>
            <MailWarning size={26} style={{ margin: '0 auto 8px' }} />
            {(dados?.cientes ?? 0) > 0
              ? `Nada pendente no periodo. ${contar(dados?.cientes ?? 0, 'mensagem')} fora da lista por "Ciente".`
              : 'Tudo confirmado no periodo. Nenhuma mensagem sem recibo de entrega.'}
          </div>
        )}

        {!carregando && grupos.map((g) => {
          const primeiro = g.primeiro;
          const anexos = g.itens.filter((i) => i.tipo !== 'text').length;
          const Icone = primeiro.inbox === 'compras' ? ShoppingCart : Headphones;
          const pendentes = g.itens.filter((i) => !i.ciente_em);
          const grupoEmVoo = g.itens.some((i) => ocupados.has(i.id));
          return (
            <div key={g.chave} style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {/* O cabecalho era UM <button> so (a linha inteira abria a conversa). Com a acao
                  de grupo ele virou uma faixa flex com DOIS botoes irmaos: botao dentro de botao
                  e HTML invalido e o clique do filho borbulharia pro pai, abrindo a conversa
                  junto — o operador clicaria em "Ciente para todos" e sairia do painel. */}
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'var(--page-bg)', borderBottom: '1px solid var(--border)', padding: '7px 10px',
                }}
              >
                <button
                  type="button"
                  onClick={() => abrirConversa(g.chave)}
                  title="Abrir a conversa"
                  style={{
                    flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left',
                    background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--text-color)',
                  }}
                >
                  <Avatar nome={primeiro.nome || primeiro.telefone || '?'} fotoUrl={primeiro.foto_url} tamanho={28} />
                  <span style={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {ident(primeiro)}
                  </span>
                  <Icone size={13} style={{ opacity: 0.7, flexShrink: 0 }} />
                </button>

                {/* Acao do CONTATO, no cabecalho do contato. Some quando nao ha o que marcar e
                    vira "Desfazer todos" no grupo inteiro ja ciente (so visivel com o "Mostrar
                    cientes") — sem essa volta, desfazer 20 linhas seria 20 cliques. */}
                {pendentes.length > 0 ? (
                  <button
                    type="button"
                    disabled={grupoEmVoo}
                    onClick={() => cienteDoGrupo(primeiro, pendentes)}
                    title={`Marcar como ciente as ${pendentes.length} desta conversa`}
                    style={{ ...btnNeutro, flexShrink: 0, background: 'var(--panel-bg)' }}
                  >
                    <Check size={13} /> Ciente para todos{g.itens.length !== pendentes.length ? ` (${pendentes.length})` : ''}
                  </button>
                ) : (
                  <button
                    type="button"
                    disabled={grupoEmVoo}
                    onClick={() => desfazerDoGrupo(g.itens)}
                    title="Devolver todas estas mensagens pra lista"
                    style={{ ...btnNeutro, flexShrink: 0, background: 'var(--panel-bg)' }}
                  >
                    <Undo2 size={13} /> Desfazer todos
                  </button>
                )}

                <span style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  {anexos > 0 && (
                    // Anexo primeiro: orcamento ou nota que nao chegou e o que perde venda.
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: 700 }}>
                      <Paperclip size={12} /> {anexos}
                    </span>
                  )}
                  <span style={{ opacity: 0.8 }}>{g.itens.length} sem confirmar</span>
                </span>
              </div>

              {/* overflow-x auto, nunca hidden: a coluna de acoes e a ultima, e cortada em
                  silencio deixaria o botao inalcancavel justo no tablet. */}
              <div style={{ overflowX: 'auto' }}>
                <table className="tabela" style={{ width: '100%', fontSize: 12 }}>
                  <tbody>
                    {g.itens.map((it) => {
                      const emVoo = ocupados.has(it.id);
                      const jaCiente = !!it.ciente_em;
                      return (
                        <tr key={it.id} style={jaCiente ? { opacity: 0.55 } : undefined}>
                          <td style={{ whiteSpace: 'nowrap', width: 1, paddingRight: 10 }}>{it.quando}</td>
                          <td style={{ whiteSpace: 'nowrap', width: 1, paddingRight: 10, opacity: 0.75 }}>
                            ha {idade(it.idade_s)}
                          </td>
                          <td style={{ maxWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {it.tipo !== 'text' && <Paperclip size={11} style={{ display: 'inline', marginRight: 4, marginBottom: -1 }} />}
                            {it.preview || <i style={{ opacity: 0.6 }}>{it.tipo}</i>}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', width: 1, textAlign: 'right', opacity: 0.75 }}>
                            {it.enviado_por ? it.enviado_por.replace(/\s+FIODECOBRE$/i, '') : '—'}
                          </td>
                          <td style={{ whiteSpace: 'nowrap', width: 1, textAlign: 'right', padding: '4px 6px' }}>
                            {jaCiente ? (
                              // A linha ciente diz POR QUE saiu da lista: "conferi" e "mandei de
                              // novo" nao sao a mesma historia pra quem vier ler depois.
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 11 }}>
                                  {it.ciente_motivo === 'reenviada' ? 'Reenviada' : 'Ciente'}
                                  {it.ciente_por ? ` · ${it.ciente_por.replace(/\s+FIODECOBRE$/i, '')}` : ''}
                                </span>
                                <button type="button" disabled={emVoo} onClick={() => marcarCiente(it, false)} style={btnNeutro} title="Voltar pra lista">
                                  <Undo2 size={13} /> Desfazer
                                </button>
                              </span>
                            ) : confirmadasAgora.has(it.id) ? (
                              // O recibo chegou com o painel aberto. Saber QUAIS confirmaram e o
                              // que impede o operador de reenviar por cima de mensagem entregue —
                              // o backend recusaria (409), mas fazer ele bater na trava pra
                              // descobrir seria esconder o que a tela ja sabe.
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700 }}>
                                <Check size={13} /> Entregue agora — sai no Atualizar
                              </span>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <button type="button" disabled={emVoo} onClick={() => marcarCiente(it, true)} style={btnNeutro} title="Tirar da lista — ja resolvi esta">
                                  <Check size={13} /> Ciente
                                </button>
                                <button type="button" disabled={emVoo} onClick={() => reenviar(it)} style={btnPrimario} title="Mandar esta mensagem de novo">
                                  {emVoo ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Reenviar
                                </button>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })}

        {dados?.truncado && (
          // Teto nunca some calado: lista cortada sem aviso le como "e so isso".
          <p style={{ fontSize: 12, color: 'var(--text-color)', opacity: 0.75 }}>
            Mostrando as 300 mais recentes — ha mais no periodo escolhido.
          </p>
        )}

        {/* O rodape existe pra impedir a leitura errada do titulo. Sem ele, "nao entregues" vira
            veredito e a atendente reenvia por cima de mensagem que chegou. */}
        <p style={{ fontSize: 12, color: 'var(--text-color)', opacity: 0.75, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
          Estas mensagens sairam daqui e o WhatsApp ainda <b>nao confirmou</b> a entrega. Boa parte
          chega depois — celular desligado confirma quando volta. O que decide se ha problema de
          verdade e a taxa do canal, no topo deste painel. <b>Ciente</b> so tira a linha da lista
          (nao avisa o cliente); <b>Reenviar</b> manda a mesma mensagem outra vez.
        </p>
      </div>
    </Modal>
  );
}

// Botoes da linha: pequenos por caberem numa tabela de 12px, mas BOTOES — acao primaria dentro
// de grade e botao solido, nao link. Azul de tema (`--primary`), nunca verde (daltonismo).
const btnBase: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap',
  borderRadius: 6, padding: '3px 8px', fontSize: 11, fontWeight: 600, cursor: 'pointer', lineHeight: 1.4,
};
const btnNeutro: CSSProperties = {
  ...btnBase, background: 'transparent', color: 'var(--text-color)', border: '1px solid var(--border)',
};
const btnPrimario: CSSProperties = {
  ...btnBase, background: 'var(--primary)', color: '#fff', border: '1px solid var(--primary)',
};
